import {
  DecisionPageLayout,
  DecisionSection,
  DecisionSubHeading,
  DecisionCard,
  DecisionList,
  CrisisBanner,
} from "@/components/DecisionPageLayout";
import { InlineRelatedLinks } from "@/components/RelatedPages";
import { Link } from "@/lib/router-compat";
import { Phone } from "lucide-react";
import { Button } from "@/components/ui/button";
const SpravatoWhatToExpect = () => {
  return (
    <DecisionPageLayout
      seo={{
        title:
          "What to Expect at Your First SPRAVATO® Session | Neuro Wellness TMS Centers of America",
        description:
          "A step-by-step walkthrough of your first SPRAVATO® treatment session at Neuro Wellness TMS Centers of America in Coral Springs, FL — from arrival through monitored discharge.",
        canonical: "/spravato-what-to-expect",
        keywords:
          "SPRAVATO first session, what to expect SPRAVATO, esketamine treatment Coral Springs, SPRAVATO REMS center Florida",
        jsonLd: {
          "@context": "https://schema.org",
          "@type": "MedicalProcedure",
          name: "SPRAVATO® (Esketamine) Treatment Session",
          procedureType: "Nasal spray administration under REMS supervision",
          howPerformed:
            "Patient self-administers esketamine nasal spray in a certified healthcare setting, followed by a minimum 2-hour monitored observation period.",
          bodyLocation: "Nasal / outpatient clinic",
          preparation:
            "No food for 2 hours before appointment, no liquids for 30 minutes before. Arrange a ride home — you cannot drive after treatment.",
          followup:
            "Plan for about 3 hours total from arrival to discharge. Do not drive or operate machinery until the next day after a restful night's sleep.",
          provider: {
            "@id": "https://neurowellnesstms.com/#organization",
          },
        },
      }}
      badge="SPRAVATO® · Patient Guide"
      title="What to Expect at Your First SPRAVATO® Session"
      subtitle="Most of the anxiety about SPRAVATO® comes from not knowing what the experience is like. So here it is, step by step — what actually happens from the moment you walk in."
      ctaHeading="Have Questions Before You Come In?"
      ctaText="That's normal and expected. Schedule a free phone consultation and we'll walk you through every detail before you commit to anything."
    >
      {/* Lead — ride arrangement */}
      <DecisionSection heading="Arrange your ride before you come">
        <p>
          This is the one detail you need to settle before anything else. You
          cannot drive yourself home after a SPRAVATO® session, and typically
          not until the next day after a full night's sleep. Line up a family
          member, friend, or rideshare before your appointment.
        </p>
        <p>
          If you arrive without a plan to get home, we won't be able to proceed
          with treatment that day. It's that important — and it's a federal REMS
          requirement, not just our policy.
        </p>
      </DecisionSection>

      {/* Time + food */}
      <DecisionSection heading="Plan for about three hours total">
        <p>
          From the moment you check in to the moment you're discharged, expect
          the visit to take roughly three hours. Most of that time is the
          monitored observation period required by the FDA at every certified
          SPRAVATO® center.
        </p>
        <DecisionSubHeading>Eat lightly beforehand</DecisionSubHeading>
        <p>
          Don't eat for at least two hours before your appointment, and don't
          drink anything for at least 30 minutes before. This reduces the chance
          of nausea, which is one of the more common side effects. If you use a
          nasal steroid or decongestant, take it at least an hour before your
          visit.
        </p>
      </DecisionSection>

      {/* Step-by-step walkthrough */}
      <DecisionSection heading="What happens during your session">
        <p>
          Here's the sequence, start to finish. Nothing here is rushed, and our
          clinical team stays with you throughout.
        </p>
      </DecisionSection>

      {/* Step 1 */}
      <DecisionCard className="border-l-4 border-l-accent">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
            1
          </span>
          <h3 className="font-headings text-lg font-bold text-primary">
            Blood pressure check on arrival
          </h3>
        </div>
        <p className="text-foreground/85 text-base leading-relaxed">
          When you arrive, we'll check your blood pressure and review how you've
          been doing since your last visit. SPRAVATO® can raise blood pressure
          temporarily, so we take a reading before each session.
        </p>
      </DecisionCard>

      {/* Step 2 */}
      <DecisionCard className="border-l-4 border-l-accent">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
            2
          </span>
          <h3 className="font-headings text-lg font-bold text-primary">
            A clinician demonstrates the nasal spray device
          </h3>
        </div>
        <p className="text-foreground/85 text-base leading-relaxed">
          Before you use it yourself, a member of our clinical team shows you
          exactly how the device works — how to hold it, how to spray, and what
          to expect. You'll have the chance to ask anything before you start.
        </p>
      </DecisionCard>

      {/* Step 3 */}
      <DecisionCard className="border-l-4 border-l-accent">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
            3
          </span>
          <h3 className="font-headings text-lg font-bold text-primary">
            You self-administer the spray
          </h3>
        </div>
        <p className="text-foreground/85 text-base leading-relaxed">
          You spray the medication yourself, following our team's instructions.
          A clinician is present the entire time. The dosing itself takes only a
          few minutes.
        </p>
      </DecisionCard>

      {/* Step 4 */}
      <DecisionCard className="border-l-4 border-l-accent">
        <div className="flex items-center gap-3">
          <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-accent text-sm font-bold text-white">
            4
          </span>
          <h3 className="font-headings text-lg font-bold text-primary">
            Two-hour monitored observation
          </h3>
        </div>
        <p className="text-foreground/85 text-base leading-relaxed">
          After dosing, you stay with us for at least two hours. This is
          required by the FDA at every certified SPRAVATO® center — it's not
          optional and it's not unique to us. A member of our clinical team
          monitors you throughout. We'll check your blood pressure again during
          this period.
        </p>
      </DecisionCard>

      {/* What you may feel */}
      <DecisionSection heading="What you may feel during the session">
        <p>
          Effects usually begin within about forty minutes of dosing. Here's
          what people commonly describe — not as a prediction of what you'll
          experience, but so you know what's on the table.
        </p>
        <DecisionSubHeading>
          Dissociation is the most common experience
        </DecisionSubHeading>
        <p>
          Many people describe feeling floaty, dreamlike, or briefly detached
          from themselves or their surroundings. You're still aware and able to
          communicate. Dissociation is one of the most commonly reported
          effects, and it typically begins soon after dosing.
        </p>
        <DecisionSubHeading>Drowsiness is common</DecisionSubHeading>
        <p>
          Some people feel sleepy during the observation period. Sleeping
          through part of it is fine — our team is still monitoring you. You may
          also feel calm, or simply quiet.
        </p>
      </DecisionSection>

      {/* Discharge */}
      <DecisionSection heading="Before you leave">
        <p>
          Before discharge, a clinician assesses you to confirm you're ready to
          go. When our team determines you're clear, your ride takes you home.
        </p>
        <p>
          Some people feel tired or foggy for the rest of the day. Most feel
          back to normal by the next morning. You should not drive, operate
          machinery, or do anything requiring full alertness until the next day,
          after a restful night's sleep.
        </p>
      </DecisionSection>

      {/* First session context */}
      <DecisionSection heading="This is one session in a course">
        <p>
          Your first session is part of a treatment course, not a one-off. The
          typical schedule starts with twice-weekly sessions for the first four
          weeks, then once weekly for the next four. After that, you and your
          provider decide together how to continue.
        </p>
        <p>
          You can read more about the full treatment schedule and what SPRAVATO®
          is on our{" "}
          <Link
            to="/services/spravato"
            className="font-semibold text-accent underline decoration-accent/40 underline-offset-2 hover:decoration-accent"
          >
            SPRAVATO® overview page
          </Link>
          .
        </p>
      </DecisionSection>

      {/* Quick reference card */}
      <DecisionCard>
        <h3 className="font-headings text-lg font-bold text-primary">
          Quick reference for your first visit
        </h3>
        <DecisionList
          items={[
            "Arrange your ride home before coming — you cannot drive after treatment",
            "Don't eat for 2 hours before; don't drink for 30 minutes before",
            "If you use a nasal steroid or decongestant, take it at least 1 hour before",
            "Plan for about 3 hours total from arrival to discharge",
            "You self-administer the nasal spray with a clinician present",
            "Two-hour monitored observation is required after every dose",
            "Don't drive or operate machinery until the next day after a full night's sleep",
          ]}
        />
      </DecisionCard>

      {/* Mid-page CTA */}
      <div className="rounded-2xl bg-gradient-navy p-6 sm:p-8 text-center space-y-4">
        <h3 className="font-headings text-xl font-bold text-white">
          Still have questions about what it's like?
        </h3>
        <p className="text-primary-foreground/80 text-base leading-relaxed">
          That's completely normal. We'd rather you ask everything before you
          walk in. A free phone consultation is the easiest way to get answers.
        </p>
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-1">
          <Button
            asChild
            size="lg"
            className="w-full sm:w-auto bg-gradient-accent text-accent-foreground shadow-glow hover:opacity-90"
          >
            <a href="/#consultation">Schedule a Free Phone Consultation</a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="w-full sm:w-auto border-2 border-white/50 bg-transparent text-white hover:bg-white/15 hover:border-white/70"
          >
            <a href="tel:9548279793">
              <Phone className="h-4 w-4 mr-2" />
              (954) 827-9793
            </a>
          </Button>
        </div>
      </div>

      {/* Internal links */}
      <DecisionSection heading="Related pages">
        <div className="flex flex-col gap-2">
          <Link
            to="/services/spravato"
            className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
          >
            → SPRAVATO® overview
          </Link>
          <Link
            to="/services/spravato"
            className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
          >
            → Treatment schedule
          </Link>
          <Link
            to="/spravato-what-to-expect"
            className="inline-flex items-center gap-2 text-base font-medium text-accent hover:underline"
          >
            → What it feels like
          </Link>
        </div>
      </DecisionSection>

      {/* Crisis banner */}
      <CrisisBanner />

      <InlineRelatedLinks
        heading="Explore Related Pages"
        links={[
          {
            title: "SPRAVATO® Overview",
            href: "/services/spravato",
          },
          {
            title: "Cost & Insurance Options",
            href: "/cost-insurance",
          },
          {
            title: "How Insurance Works",
            href: "/how-insurance-works",
          },
          {
            title: "Am I a Candidate?",
            href: "/am-i-a-candidate",
          },
          {
            title: "Compare All Treatments",
            href: "/compare-treatments",
          },
          {
            title: "SAINT® Accelerated TMS",
            href: "/saint",
          },
        ]}
      />
    </DecisionPageLayout>
  );
};
export default SpravatoWhatToExpect;
